/** @jsxImportSource @emotion/react */
import {
  Button,
  Drawer,
  type DrawerProps,
  Modal as MUIModal,
  type ModalProps as MUIModalProps,
} from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import type { ReactElement } from 'react';

import config from 'config';

import { cn } from '@endure/ui';
import { themeStore } from 'containers/Layout/themeStore';
import { useIsSmDown } from 'hooks/responsive';
import { useTranslation } from 'libs/translations';
import { Icon } from '../Icon';
import { useModalStyles } from './styles';

export interface ModalProps extends Omit<MUIModalProps, 'title' | 'open'> {
  className?: string;
  buttonClassName?: string;
  isOpen: boolean;
  handleClose: () => void;
  handleBackAction?: () => void;
  title?: string | ReactElement | ReactElement[];
  noHorizontalPadding?: boolean;
  useDrawerInXs?: boolean;
  anchor?: DrawerProps['anchor'];
  isCloseDisabled?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  className,
  buttonClassName,
  children,
  handleClose,
  handleBackAction,
  isOpen,
  title,
  noHorizontalPadding,
  useDrawerInXs,
  anchor = 'bottom',
  isCloseDisabled = false,
  ...otherModalProps
}) => {
  const { t } = useTranslation();
  const s = useModalStyles({
    hasTitleComponent: Boolean(title),
    noHorizontalPadding,
  });
  const isMobile = useIsSmDown();
  const theme = themeStore.use.theme();

  const showDrawer = isMobile && useDrawerInXs;

  const handleModalClose = () => {
    if (isCloseDisabled) return;
    handleClose();
  };

  const dom = (
    <div
      className={cn(
        `forge-${theme}-modal flex flex-col overflow-auto outline-hidden rounded-xl border border-line bg-elevated text-foreground shadow-large`,
        showDrawer
          ? 'rounded-b-none'
          : 'absolute top-[50%] left-[50%] translate-y-[-50%] translate-x-[-50%] max-w-136 w-[calc(100%-2rem)] max-h-[calc(100%-2rem)]',
        className,
      )}
    >
      <div css={s.titleWrapper}>
        {!!handleBackAction && (
          <Button
            css={s.backAction}
            className={buttonClassName}
            disableRipple
            onClick={handleBackAction}
            aria-label={t('modal.backAriaLabel')}
          >
            <Icon css={s.backArrow} name="arrowRight" />
          </Button>
        )}
        <div css={s.titleComponent}>{title}</div>

        <Button
          css={s.closeIcon}
          className={cn('right-6', buttonClassName)}
          disableRipple
          onClick={handleModalClose}
          aria-label={t('modal.closeAriaLabel')}
          disabled={isCloseDisabled}
        >
          <Icon name="close" className="size-6" />
        </Button>
      </div>

      <div css={s.contentWrapper}>{children as React.ReactNode}</div>
    </div>
  );

  return showDrawer ? (
    <Drawer anchor={anchor} open={isOpen} onClose={handleModalClose}>
      {dom}
    </Drawer>
  ) : (
    <MUIModal
      open={isOpen}
      onClose={handleModalClose}
      onBackdropClick={handleModalClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      disablePortal={config.environment === 'storybook'}
      {...otherModalProps}
    >
      <Fade in={isOpen}>{dom}</Fade>
    </MUIModal>
  );
};
